import { createClient } from '@supabase/supabase-js';

// Supabase configuration
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase URL or Anon Key is missing. Please check your environment variables.');
}

// Create Supabase client
export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
});

export type Database = {
  public: {
    Tables: {
      clients: {
        Row: {
          id: number;
          name: string;
          type: string;
          sector: string;
          contact: string;
          email: string;
          phone: string;
          active_projects: number;
          total_revenue: number;
          last_contact: string | null;
          locale: 'en' | 'es';
          address: string | null;
          auth_user_id: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<
          Database['public']['Tables']['clients']['Row'],
          'id' | 'created_at' | 'updated_at' | 'auth_user_id'
        > & {
          auth_user_id?: string | null;
        };
        Update: Partial<Database['public']['Tables']['clients']['Insert']>;
      };
      projects: {
        Row: {
          id: number;
          name: string;
          client_id: number;
          description: string | null;
          type: string | null;
          status: string;
          stage: string | null;
          priority: string | null;
          start_date: string | null;
          end_date: string | null;
          due_date: string | null;
          budget: number | null;
          actual_cost: number | null;
          progress: number;
          github_repo_url: string | null;
          netlify_site_id: string | null;
          share_content: Record<string, any> | null;
          share_token: string | null;
          share_status: 'draft' | 'shared' | 'changes_requested' | 'approved';
          approved_at: string | null;
          approved_name: string | null;
          approved_email: string | null;
          approved_snapshot: Record<string, any> | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<
          Database['public']['Tables']['projects']['Row'],
          | 'id'
          | 'created_at'
          | 'updated_at'
          | 'share_token'
          | 'share_status'
          | 'approved_at'
          | 'approved_name'
          | 'approved_email'
          | 'approved_snapshot'
        > & {
          share_token?: string | null;
          share_status?: 'draft' | 'shared' | 'changes_requested' | 'approved';
          approved_at?: string | null;
          approved_name?: string | null;
          approved_email?: string | null;
          approved_snapshot?: Record<string, any> | null;
        };
        Update: Partial<Database['public']['Tables']['projects']['Insert']>;
      };
      project_feedback: {
        Row: {
          id: number;
          project_id: number;
          kind: 'comment' | 'change_request' | 'approval';
          body: string | null;
          author_name: string | null;
          author_email: string | null;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['project_feedback']['Row'], 'id' | 'created_at'> & {
          created_at?: string;
        };
        Update: Partial<Database['public']['Tables']['project_feedback']['Insert']>;
      };
      tasks: {
        Row: {
          id: number;
          project_id: number;
          title: string;
          description: string | null;
          assigned_to: string | null;
          status: string;
          due_date: string | null;
          task_budget: number | null;
          actual_cost: number | null;
          task_order: number;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['tasks']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['tasks']['Insert']>;
      };
      quotes: {
        Row: {
          id: string;
          quote_number: string | null;
          client_id: number;
          project_id: number | null;
          amount: number;
          status: string;
          issue_date: string | null;
          sent_date: string | null;
          valid_until: string;
          currency: string | null;
          notes: string | null;
          terms_conditions: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['quotes']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['quotes']['Insert']>;
      };
      quote_line_items: {
        Row: {
          id: number;
          quote_id: string;
          name: string;
          description: string | null;
          quantity: number;
          unit_price: number;
          tax_rate: number | null;
          total: number;
          item_order: number;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['quote_line_items']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['quote_line_items']['Insert']>;
      };
      invoices: {
        Row: {
          id: string;
          client_id: number;
          project_id: number | null;
          amount: number;
          status: string;
          issue_date: string;
          due_date: string;
          paid_date: string | null;
          payment_url: string | null;
          payment_provider: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<
          Database['public']['Tables']['invoices']['Row'],
          'created_at' | 'updated_at' | 'payment_url' | 'payment_provider'
        > & {
          payment_url?: string | null;
          payment_provider?: string | null;
        };
        Update: Partial<Database['public']['Tables']['invoices']['Insert']>;
      };
      payments: {
        Row: {
          id: number;
          invoice_id: string;
          amount: number;
          paid_at: string;
          method: string | null;
          mercury_transaction_id: string | null;
          status: string;
          raw_note: string | null;
          created_at: string;
        };
        Insert: Omit<Database['public']['Tables']['payments']['Row'], 'id' | 'created_at'>;
        Update: Partial<Database['public']['Tables']['payments']['Insert']>;
      };
      landings: {
        Row: {
          id: number;
          name: string;
          campaign: string | null;
          status: string;
          url: string;
          is_public: boolean;
          blocks: number;
          views: number;
          seo_metadata: Record<string, any> | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['landings']['Row'], 'id' | 'created_at' | 'updated_at' | 'views' | 'blocks' | 'seo_metadata'> & {
          blocks?: number;
          seo_metadata?: Record<string, any> | null;
        };
        Update: Partial<Database['public']['Tables']['landings']['Insert']>;
      };
      landing_blocks: {
        Row: {
          id: number;
          landing_id: number;
          type: string;
          block_order: number;
          content: Record<string, any>;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['landing_blocks']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['landing_blocks']['Insert']>;
      };
      website_content: {
        Row: {
          id: number;
          section: string;
          content: Record<string, any>;
          seo_metadata: Record<string, any> | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['website_content']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['website_content']['Insert']>;
      };
      team_members: {
        Row: {
          id: number;
          name: string;
          email: string | null;
          phone: string | null;
          role: string | null;
          bio: string | null;
          avatar_url: string | null;
          is_active: boolean;
          auth_user_id: string | null;
          auth_invited_at: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<
          Database['public']['Tables']['team_members']['Row'],
          'id' | 'created_at' | 'updated_at' | 'auth_user_id' | 'auth_invited_at'
        > & {
          auth_user_id?: string | null;
          auth_invited_at?: string | null;
        };
        Update: Partial<Database['public']['Tables']['team_members']['Insert']>;
      };
      project_team_assignments: {
        Row: {
          id: number;
          project_id: number;
          team_member_id: number;
          participation_percentage: number | null;
          estimated_revenue: number | null;
          notes: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['project_team_assignments']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['project_team_assignments']['Insert']>;
      };
      leads: {
        Row: {
          id: number;
          name: string;
          email: string;
          phone: string | null;
          company: string | null;
          budget: string | null;
          product_interest?: string | null;
          message: string | null;
          source: string | null;
          landing_url: string | null;
          locale: string | null;
          status: string;
          notes: string | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['leads']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['leads']['Insert']>;
      };
      cashflow_entries: {
        Row: {
          id: number;
          entry_type: 'income' | 'expense';
          category: string;
          amount: number;
          currency: string | null;
          entry_date: string;
          description: string | null;
          invoice_id: string | null;
          client_id: number | null;
          created_at: string;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['cashflow_entries']['Row'], 'id' | 'created_at' | 'updated_at'>;
        Update: Partial<Database['public']['Tables']['cashflow_entries']['Insert']>;
      };
      studio_settings: {
        Row: {
          id: number;
          settings: Record<string, unknown>;
          updated_at: string;
        };
        Insert: Omit<Database['public']['Tables']['studio_settings']['Row'], 'updated_at'>;
        Update: Partial<Database['public']['Tables']['studio_settings']['Insert']>;
      };
      content_versions: {
        Row: {
          id: number;
          content_type: string;
          content_id: string;
          version_number: number;
          content_data: Record<string, unknown>;
          created_by: string | null;
          created_at: string;
          notes: string | null;
        };
        Insert: Omit<Database['public']['Tables']['content_versions']['Row'], 'id' | 'created_at'>;
        Update: Partial<Database['public']['Tables']['content_versions']['Insert']>;
      };
    };
  };
};

